'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Styrofoam Takeout Containers Release 148× More Microplastics Than Plastic Ones (2026)"
      author="MicroPlastics Research Desk"
      date="July 11, 2026"
      readTime="11 min read"
      heroImage="/blog/heroes/food.webp"
      heroImageAlt="Hot takeout food in an expanded polystyrene foam container, foam released about 148× more microplastics than polypropylene under hot water"
    >
      <BlogLastUpdated iso="2026-07-11" formatted="July 11, 2026" />

      <BlogTLDR>
        <strong>Foam is in a different league from the other takeout plastics, and hot food is what makes it so.</strong> When researchers filled
        takeout containers with hot water and identified the released particles
        by micro-Raman spectroscopy, polypropylene gave up{' '}
        <strong>1.90 × 10⁴ particles per litre</strong>, polyethylene{' '}
        <strong>1.01 × 10⁵</strong>, and <strong>expanded polystyrene foam
        2.82 × 10⁶, roughly 148× more than polypropylene</strong> (Hu et al.
        2023). Over <strong>96% were smaller than 10 µm</strong>. There is a
        clean physical reason: release is driven by polymer chains relaxing once
        the plastic crosses into its <strong>rubbery state</strong>, and{' '}
        <strong>polystyrene&apos;s glass transition sits at roughly
        100°C</strong>, the temperature of the soup you just poured in. Change
        one habit and make it this: <strong>get hot food out of the
        foam.</strong> Tip it onto a ceramic plate before you eat, and never
        reheat in the container.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Under hot-water testing, <strong>EPS foam released 2.82 × 10⁶ microplastic particles per litre</strong> versus <strong>1.90 × 10⁴ for polypropylene</strong>, a <strong>148× difference</strong> between the two most common takeout containers (Hu et al. 2023).</>,
          <>The particles are small: <strong>0.8–38 µm, with more than 96% under 10 µm</strong>, the range biology has the hardest time excluding.</>,
          <>The mechanism is thermal, not magical: release is attributed to <strong>&ldquo;relaxations of the polymer chains in the rubbery state&rdquo;</strong> plus <strong>&ldquo;defects caused by processing techniques.&rdquo;</strong></>,
          <><strong>Polystyrene&apos;s glass transition is ≈100°C</strong>, hot soup, hot noodles and a microwave all land at or above it. Polypropylene&apos;s is ≈−20 to −10°C, but PP is semi-crystalline and melts at 160–170°C, so it stays stable at food temperatures.</>,
          <>Styrene, the monomer PS is built from, also migrates, <strong>2.58 µg/L</strong> into food simulants, peaking under <strong>alkaline, 100°C, long-contact conditions</strong> (Wang et al. 2023). The US National Toxicology Program lists styrene as <strong>reasonably anticipated to be a human carcinogen</strong>.</>,
          <>Regulators are more measured: EFSA&apos;s 2025 review found release real but <strong>measured levels very low</strong>, and the FDA says current evidence does not demonstrate a health risk. <strong>The mechanism is well established; the dose-response is not.</strong></>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_styrofoam" />

      <KeyStats
        heading="Takeout containers under hot water, the numbers"
        stats={[
          { value: '2.82 × 10⁶', label: 'particles/L from EPS foam', context: 'expanded polystyrene takeout containers under hot water, identified by micro-Raman spectroscopy', source: 'Hu et al. 2023, Food Addit. Contam. Part A', sourceUrl: 'https://doi.org/10.1080/19440049.2022.2157894' },
          { value: '1.90 × 10⁴', label: 'particles/L from polypropylene', context: 'the rigid PP containers most restaurants use, about 148× fewer particles than foam in the same test', source: 'Hu et al. 2023, Food Addit. Contam. Part A', sourceUrl: 'https://doi.org/10.1080/19440049.2022.2157894' },
          { value: '>96%', label: 'of released particles under 10 µm', context: 'released particles spanned 0.8–38 µm, but the overwhelming majority sat at the small end', source: 'Hu et al. 2023, Food Addit. Contam. Part A', sourceUrl: 'https://doi.org/10.1080/19440049.2022.2157894' },
          { value: '≈100°C', label: 'polystyrene glass transition', context: 'the point at which PS chains become mobile, and the temperature of hot soup and microwaved food', source: 'Hu et al. 2023 (thermal properties)', sourceUrl: 'https://doi.org/10.1080/19440049.2022.2157894' },
          { value: '2.58 µg/L', label: 'styrene monomer migration', context: 'into food simulants from PS food containers; particle release peaked at pH 9, 100°C, 6 hours', source: 'Wang et al. 2023, Heliyon', sourceUrl: 'https://doi.org/10.1016/j.heliyon.2023.e15787' },
          { value: '210 of 210', label: 'PP containers already holding microplastics', context: 'every container sampled across 7 cities held 3–43 particles before any heating, manufacturing residue, not heat release', source: 'Hu et al. 2022, Toxics', sourceUrl: 'https://doi.org/10.3390/toxics10120762' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The 148× number, and what was actually measured
      </h2>
      <p>
        Most microplastic headlines compare a plastic to nothing. This one
        compares plastics to each other, which is far more useful, because
        &ldquo;stop eating takeout&rdquo; is not advice anyone follows, and
        &ldquo;ask for a different container&rdquo; is. Hu and colleagues, in{' '}
        <em>Food Additives &amp; Contaminants: Part A</em> (2023), took
        commercial takeout containers in three common materials, treated them
        with hot water, and identified the released particles by{' '}
        <strong>micro-Raman spectroscopy</strong>, which matters, because Raman
        confirms a particle really is the polymer in question rather than a
        stray fibre from the lab. Their averages:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Polypropylene (PP):</strong> 1.90 × 10⁴ particles/L, about 19,000.</li>
        <li><strong>Polyethylene (PE):</strong> 1.01 × 10⁵ particles/L, roughly 5× PP.</li>
        <li><strong>Expanded polystyrene (EPS / foam):</strong> 2.82 × 10⁶ particles/L, roughly <strong>148× PP</strong>.</li>
      </ul>
      <p>
        The size distribution is the part that gets skipped. Released particles
        ranged from <strong>0.8 to 38 µm</strong>, and{' '}
        <strong>more than 96% were smaller than 10 µm</strong>, the range
        biology has the hardest time excluding, and the range older
        filter-and-count methods systematically missed.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why foam is so much worse: the glass transition is the whole story
      </h2>
      <p>
        A 148× gap sounds like a scare statistic until you understand the
        mechanism, at which point it becomes something better: a prediction. The
        authors attribute release to{' '}
        <strong>&ldquo;relaxations of the polymer chains in the rubbery
        state&rdquo;</strong> plus{' '}
        <strong>&ldquo;defects caused by processing techniques.&rdquo;</strong>{' '}
        Translated: every polymer has a <strong>glass transition temperature
        (Tg)</strong>, the point at which its chains stop being locked in place
        and start being able to move. Below Tg the plastic is glassy and rigid.
        Above it, the chains relax, the surface becomes mobile, and any flaw
        built in during manufacturing works itself loose. Now put the numbers
        side by side:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Polystyrene: Tg ≈ 100°C.</strong> It melts around 240°C, but the release-relevant threshold sits at almost exactly the boiling point of water.</li>
        <li><strong>Polypropylene: Tg ≈ −20 to −10°C</strong>, melting at 160–170°C. PP is already above its Tg on your counter, but it is <em>semi-crystalline</em>, crystalline domains hold the structure together, and hot soup gets it nowhere near melting.</li>
        <li><strong>Polyethylene: Tg far below room temperature</strong>, melting at 105–130°C, closer to hot-food temperature than PP, which fits its middle position in the results.</li>
      </ul>
      <p>
        So when you pour 95°C soup into a foam container, you are not simply
        putting hot food in plastic. You are taking that specific polymer{' '}
        <strong>to the exact temperature at which its chains become
        mobile</strong>, and doing it to a material that is mostly surface area
        and mostly manufacturing defect. Expanded polystyrene is blown from
        beads fused with steam; every fusion line is a structural weak point by
        construction. Foam is not &ldquo;polystyrene, but lighter.&rdquo; It is
        polystyrene with the worst possible geometry for shedding, held at the
        worst possible temperature.
      </p>
      <p>
        Which points at something worth carrying into every plastic question you
        have: <strong>temperature is the master variable, not the heating
        method</strong>, the same conclusion published critics of the
        microwave-container literature have reached. A microwave is just the
        fastest way to make plastic hot. Foam doesn&apos;t need one; a ladle of
        hot pho does it.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Styrene, the monomer, and the alkaline complication
      </h2>
      <p>
        Particles are only half the story. Polystyrene is built from{' '}
        <strong>styrene</strong>, and styrene migrates. Wang and colleagues
        (<em>Heliyon</em>, 2023) measured{' '}
        <strong>2.58 µg/L of styrene monomer</strong> moving into food
        simulants, and found <strong>PS particle release peaking at pH 9,
        100°C, and 6 hours of contact</strong>. Alkaline conditions accelerated
        it markedly. That cuts against the intuition most of us carry, which is
        that <em>acidic</em> food is the dangerous kind. And
        alkaline-hot-and-long is an everyday combination: a foam container of
        hot soup, congee, or alkaline noodles sitting in a delivery bag for 45
        minutes, then in the fridge until tomorrow.
      </p>
      <p>
        On styrene itself, precision matters more than volume. The{' '}
        <strong>US National Toxicology Program lists styrene as
        &ldquo;reasonably anticipated to be a human carcinogen&rdquo;</strong>{' '}
        in its Report on Carcinogens, first listed in 2011 and carried into the
        current edition, on the basis of limited evidence in humans (elevated
        lymphohematopoietic cancer among occupationally exposed workers),
        sufficient evidence in animals, and supporting mechanistic data. Two
        things are true at once. That classification is real and not a fringe
        opinion, and it was derived overwhelmingly from{' '}
        <strong>occupational inhalation exposure</strong> among
        reinforced-plastics workers, not from people eating soup. Migration from
        a container is microgram-per-litre scale. Prefer a different container;
        don&apos;t be frightened of last night&apos;s dinner.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The containers already hold microplastics, but read this carefully
      </h2>
      <p>
        A companion study by the same lead author (Hu et al. 2022, <em>Toxics</em>)
        sampled <strong>210 polypropylene takeout containers across seven
        cities</strong> and found <strong>every single one already contained
        microplastics. 3 to 43 particles each</strong>. Content farms will tell
        you your takeout box comes &ldquo;pre-loaded with plastic.&rdquo; The
        honest framing: this counted particles{' '}
        <strong>already sitting inside the container from manufacturing</strong>,
        which rinse out when it is washed or filled. It is <strong>not</strong>{' '}
        a measure of heat-generated release, and the counts, single or double
        digits, are orders of magnitude below the hot-water figures above.
        Useful context. Not the headline.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Never microwave foam, and &ldquo;microwave safe&rdquo; wouldn&apos;t save you anyway
      </h2>
      <p>
        The easiest rule here: <strong>do not put an EPS foam container in the
        microwave.</strong> Microwaved food routinely reaches or exceeds 100°C in
        spots, which is <em>at or above polystyrene&apos;s glass
        transition</em>, you would be driving the polymer straight through the
        threshold where release switches on, in a material already primed to
        shed. The visible warping and pitting people report is just the
        macroscopic version of what is happening at particle scale.
      </p>
      <p>
        And don&apos;t let a &ldquo;microwave safe&rdquo; stamp on a different
        container reassure you too much either.{' '}
        <Link href="/blog/what-does-microwave-safe-actually-mean" className="text-[#1E6B47] underline hover:text-[#155235]">As our microwave-safe explainer sets out</Link>,
        that phrase is not an FDA certification. It reflects that a material has
        been cleared for a food-contact condition of use, testing that measures{' '}
        <strong>chemical migration into food simulants</strong>, plus the
        container&apos;s ability to survive heat without melting or deforming.{' '}
        <strong>No part of that testing counts plastic particles.</strong> A
        container can be fully compliant and still shed micro- and nanoplastics,
        because particle release is not what the test looks for. The same logic
        applies to{' '}
        <Link href="/blog/can-you-microwave-ziploc-bags" className="text-[#1E6B47] underline hover:text-[#155235]">microwaving Ziploc bags</Link>{' '}
        and everything else in the reheating drawer.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the regulators say, the counterweight we&apos;re obliged to print
      </h2>
      <p>
        A site that prints only the scary half of the evidence is a content
        farm. So: in 2025 <strong>EFSA</strong> published a literature review on
        micro- and nanoplastic release from food-contact materials, screening
        over 1,700 publications and extracting data from 122, and its
        conclusions cut against the headline. Release <strong>does
        occur</strong>, but EFSA judged it driven <strong>primarily by physical
        wear</strong> (abrasion, cutting, friction) rather than principally by
        heat; found <strong>measured release levels to be very low</strong>; and
        warned that contamination and particle misidentification mean the
        literature may <strong>overestimate</strong> release. Its key line:{' '}
        <em>there is no sufficient basis at this stage to estimate
        micro/nanoplastic exposure from food-contact materials during their
        use.</em> The <strong>FDA&apos;s</strong> position is similarly
        restrained, current evidence does not demonstrate that the levels of
        microplastics detected in foods pose a risk to human health.
      </p>
      <p>
        Both things are true. <strong>The 148× gap is real and well-measured.
        What it means for your body is genuinely not established.</strong> Which
        is exactly why the advice here is a free, thirty-second habit change
        rather than a panic: the cost of acting is near zero, and the
        uncertainty runs in only one direction.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Every takeout container, ranked
      </h2>

      <BlogComparisonTable
        caption="Takeout container types ranked by microplastic release under hot food (2026)"
        columns={[
          { key: 'container', label: 'Container' },
          { key: 'material', label: 'Material' },
          { key: 'release', label: 'Release under hot food' },
          { key: 'swap', label: 'What to do' },
        ]}
        rows={[
          { container: 'Foam clamshell / foam soup cup', material: 'Expanded polystyrene (EPS, #6)', release: '2.82 × 10⁶ particles/L, highest by far; Tg ≈ 100°C is hot-food temperature', swap: 'Ask for no foam. If you get it, transfer immediately, never reheat in it.' },
          { container: 'Rigid clear clamshell', material: 'Polystyrene (PS, #6)', release: 'High, same 100°C threshold, less surface area than foam', swap: 'Fine for cold salads; transfer anything hot.' },
          { container: 'Black or clear rigid container', material: 'Polypropylene (PP, #5)', release: '1.90 × 10⁴ particles/L (~148× less than foam; stable at food temperatures', swap: 'Best of the plastics) but see the black-plastic note below.' },
          { container: 'Paper soup cup / lined box', material: 'Paperboard + polyethylene lining', release: '1.01 × 10⁵ particles/L class; the PE lining melts at 105–130°C', swap: 'Better than foam, worse than PP. Decant hot liquids.' },
          { container: 'Aluminium tray, cardboard lid', material: 'Aluminium', release: 'Near zero, no polymer against the food', swap: 'Excellent. Ask for it if the restaurant offers it.' },
          { container: 'Your own glass container', material: 'Borosilicate / soda-lime glass', release: 'Zero, inert at any temperature you will reheat at', swap: 'The real answer for regular takeout. Hand it over at the counter.' },
        ]}
      />
      <p className="text-sm text-gray-500">
        One caveat on the winner: polypropylene is far better on particle
        release, but recycled black plastic carries a separate issue (flame-retardant contamination from recycled electronics) covered in{' '}
        <Link href="/blog/black-plastic-kitchen-utensils" className="text-[#1E6B47] underline hover:text-[#155235]">our black plastic breakdown</Link>.
        For the wider picture, see{' '}
        <Link href="/blog/microplastics-in-fast-food" className="text-[#1E6B47] underline hover:text-[#155235]">microplastics in fast food packaging</Link>,{' '}
        <Link href="/blog/microplastics-in-fast-food" className="text-[#1E6B47] underline hover:text-[#155235]">what&apos;s actually in fast food</Link>,
        or the{' '}
        <Link href="/microplastics-in-food" className="text-[#1E6B47] underline hover:text-[#155235]">microplastics in food hub</Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The one thing to change
      </h2>
      <p>
        If you take a single action from this article:{' '}
        <strong>get hot food out of foam.</strong> Not eventually, before you
        eat. Tip it onto a ceramic plate or into a glass bowl the moment it
        lands on the counter. Every extra minute of 90°C food sitting in
        expanded polystyrene is time spent above the polymer&apos;s glass
        transition, and release is driven by temperature <em>and</em> time. Then,
        in rough order of how much they move the needle:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Never microwave foam.</strong> The highest-release scenario in the category, and it takes ten seconds to avoid.</li>
        <li><strong>Ask for no foam when you order.</strong> A one-line note in the delivery app works more often than you&apos;d think, restaurants that let you choose usually have PP in the back.</li>
        <li><strong>Prefer the rigid PP containers.</strong> Roughly 148× less particle release under hot water. Still transfer before reheating.</li>
        <li><strong>Don&apos;t store leftovers in the takeout box.</strong> Contact time is the second half of the equation, the <em>Heliyon</em> data had PS release peaking after hours, not minutes. Move it into{' '}
          <Link href="/blog/best-plastic-free-food-storage" className="text-[#1E6B47] underline hover:text-[#155235]">glass storage</Link>.
        </li>
        <li><strong>For regular takeout, bring your own container.</strong> If you collect the same order weekly, handing over a glass dish removes the problem permanently.</li>
        <li><strong>Foam cups and plates follow the same rules</strong>, same polymer, same threshold. Ranked in our{' '}
          <Link href="/blog/red-solo-cups-plastic-party-supplies-microplastics" className="text-[#1E6B47] underline hover:text-[#155235]">party supplies guide</Link>.
        </li>
      </ul>

      <BlogWhatTheAppChecks
        items={[
          <>The resin of the container you scan (EPS foam, rigid PS, PP, or PE-lined paperboard) so &ldquo;#6 foam&rdquo; stops being invisible fine print under a lid.</>,
          <>Whether the temperature of what you&apos;re putting in it crosses that polymer&apos;s glass transition, which is what actually drives release.</>,
          <>A 0–100 risk score per item and use case, so &ldquo;cold salad in a PS clamshell&rdquo; and &ldquo;hot soup in a foam cup&rdquo; score as the very different scenarios they are.</>,
          <>The cheapest same-format swap. PP over foam, aluminium over PP, your own glass over all of it.</>,
        ]}
      />

      <BlogAppCTA
        headline="Know what your takeout arrived in before you eat out of it"
        sub="Foam, rigid polystyrene, polypropylene, PE-lined paper, they look alike in a delivery bag and behave 148× apart under hot food. Scan the container and the app tells you what it is, how it sheds at your food's temperature, and what to do in the next thirty seconds."
        buttonLabel="Scan my takeout container"
      />

      <BlogFAQ
        items={[
          {
            question: 'Are styrofoam containers safe?',
            answer:
              'For cold food, they are a minor concern. For hot food they are the worst common takeout container by a wide margin: under hot-water testing, expanded polystyrene released 2.82 million microplastic particles per litre versus 19,000 from polypropylene, roughly 148× more (Hu et al. 2023). Polystyrene’s glass transition is around 100°C, the temperature of hot soup, so hot food takes the polymer into exactly the state where it sheds. Transfer hot food to ceramic or glass before eating.',
          },
          {
            question: 'Can you microwave styrofoam?',
            answer:
              'No. Microwaved food routinely reaches 100°C, at or above polystyrene’s glass transition temperature, you would be driving the polymer straight through the threshold where particle release switches on, in a foamed structure already full of manufacturing weak points. Foam also warps and pits at those temperatures. Transfer the food to a ceramic plate or glass container and microwave that instead.',
          },
          {
            question: 'Are there microplastics in styrofoam cups?',
            answer:
              'Yes. Expanded polystyrene is the highest-shedding common food-contact plastic measured under hot conditions. 2.82 × 10⁶ particles per litre in hot-water testing, with more than 96% of them smaller than 10 µm (Hu et al. 2023). Polystyrene also releases styrene monomer, measured at 2.58 µg/L into food simulants (Wang et al. 2023). A cold drink in a foam cup is a far smaller concern than a hot one.',
          },
          {
            question: 'Are plastic takeout containers safe?',
            answer:
              'They are not all the same, and the differences are large. In the same hot-water test, polypropylene released 1.90 × 10⁴ particles per litre, polyethylene 1.01 × 10⁵, and expanded polystyrene foam 2.82 × 10⁶. The rigid PP containers most restaurants use are roughly 148 times lower than foam, making PP the best of the plastics, though recycled black plastic carries a separate flame-retardant concern. Aluminium trays and your own glass are cleaner still.',
          },
          {
            question: 'Is styrofoam bad for you?',
            answer:
              'The mechanism is well established; the health consequence is not. Polystyrene sheds far more microplastic particles into hot food than other takeout plastics, and the styrene it is built from is listed by the US National Toxicology Program as reasonably anticipated to be a human carcinogen, a classification derived mainly from occupational inhalation exposure among plastics workers, not from eating. EFSA’s 2025 review meanwhile found measured release from food-contact materials to be very low, and the FDA says current evidence does not demonstrate a risk from microplastics detected in foods. The sensible response is a free, easy swap, not alarm.',
          },
          {
            question: 'Why does polystyrene release so many more microplastics than polypropylene?',
            answer:
              'Because of where its glass transition temperature sits. Release is attributed to relaxations of the polymer chains once the plastic enters its rubbery state, plus defects introduced during manufacturing. Polystyrene’s glass transition is about 100°C, precisely hot-food temperature. Polypropylene’s is around −20 to −10°C, but PP is semi-crystalline and melts at 160–170°C, so it stays structurally stable at the temperatures food actually reaches. Expanded polystyrene compounds this: a steam-fused foam is mostly surface area and mostly bead-fusion weak points.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Hu J-L, Duan Y, Zhong H-N, et al.',
            year: 2023,
            title: 'Analysis of microplastics released from plastic take-out food containers based on thermal properties and morphology study',
            publisher: 'Food Additives & Contaminants: Part A, 40(2), 305–318',
            url: 'https://doi.org/10.1080/19440049.2022.2157894',
          },
          {
            authors: 'Wang J, Lee J, Kwon EE, Jeong S.',
            year: 2023,
            title: 'Quantitative analysis of polystyrene microplastic and styrene monomer released from plastic food containers',
            publisher: 'Heliyon, 9(5), e15787',
            url: 'https://doi.org/10.1016/j.heliyon.2023.e15787',
          },
          {
            authors: 'Hu J, Xu X, Song Y, et al.',
            year: 2022,
            title: 'Microplastics in Widely Used Polypropylene-Made Food Containers',
            publisher: 'Toxics, 10(12), 762',
            url: 'https://doi.org/10.3390/toxics10120762',
          },
          {
            authors: 'National Toxicology Program',
            year: 2021,
            title: 'Styrene: Report on Carcinogens, 15th Edition',
            publisher: 'US Department of Health and Human Services',
            url: 'https://www.ncbi.nlm.nih.gov/books/NBK590797/',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2025,
            title: 'Literature review on micro- and nanoplastic release from food contact materials during their use',
            publisher: 'EFSA Supporting Publications',
            url: 'https://doi.org/10.2903/sp.efsa.2025.EN-9733',
          },
          {
            authors: 'US Food and Drug Administration',
            year: 2024,
            title: 'Microplastics and Nanoplastics in Foods',
            publisher: 'FDA',
            url: 'https://www.fda.gov/food/environmental-contaminants-food/microplastics-and-nanoplastics-foods',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
